/**
 * Cart Versioning
 * -------------------
 * ALBAU-855 - Legacy Basket Optimisation
 *
 * Personalised cart lines carry the configuration they were built with (slot IDs, component
 * variants, image LIP keys, insert templates). Carts can live for months, so a line added under an
 * old configuration can reach checkout and the warehouse with missing or wrong data.
 *
 * Each Personalisation has a cart version, set in the Gadget configurator ("Bump cart version", or
 * "Save & bump" on a step/slot) and synced to the product metafield `personalisation.version`.
 * Personalised lines are stamped with that version as `_cart_version` inside
 * `_personalisation_base_attributes` when they are added. The stamp lives there (not as its own
 * property) because the pricing engine only carries base attributes through to checkout and the
 * order, and Klaviyo cart recovery rebuilds carts from those checkout lines.
 *
 * A line is stale when its stamp is lower than its product's current version. Lines with no stamp
 * (added before ALBAU-855) count as version 1, so they only go stale once their personalisation
 * is bumped to 2 or higher — which is how known problem products are targeted.
 *
 * Nothing is flagged unless the shop metafield `[CART] Cart Versioning Enabled` is on (kill switch).
 * The cart drawer applies the same rule in Liquid (snippets/cart-line-version-status.liquid): stale
 * lines are flagged and checkout is blocked until the customer removes them (components/cart-version-guard.ts).
 * These helpers are for JS that needs the answer for a cart it just changed (entrypoints/cart-recover.ts).
 */

/** Key inside `_personalisation_base_attributes` holding the stamp. Ends up on the order line too. */
export const CART_VERSION_KEY = '_cart_version';

/** Version assumed when a line has no stamp or a product has no version set. */
export const DEFAULT_CART_VERSION = 1;

export interface CartLine {
  key: string;
  product_id?: number | string;
  product_title?: string;
  title?: string;
  url?: string;
  properties?: Record<string, unknown> | null;
}

export interface CartVersionData {
  /** Kill switch from the shop metafield. */
  enabled: boolean;
  /** Current cart version per product ID, for the personalised products in the cart. */
  versions: Record<string, number>;
}

const isPresent = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed !== '' && trimmed !== '[]';
  }
  return true;
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const toVersion = (value: unknown): number => {
  const version = Number(value);
  return Number.isFinite(version) && version > 0 ? version : DEFAULT_CART_VERSION;
};

/**
 * Reads one key from `_personalisation_base_attributes`, which can be an object, a JSON string or a
 * Ruby hash string (`"key" => "value"`, see pricing-engine-rs run.rs) depending on how it was saved.
 */
export const readBaseAttribute = (line: CartLine, key: string): string | null => {
  const raw = line.properties?._personalisation_base_attributes;
  if (raw && typeof raw === 'object') {
    // eslint-disable-next-line security/detect-object-injection -- key is one of our constants
    const value = (raw as Record<string, unknown>)[key];
    return value === undefined || value === null ? null : String(value);
  }
  if (typeof raw !== 'string') return null;

  // Matches "key":"value", "key": 1 and "key" => "value".
  // eslint-disable-next-line security/detect-non-literal-regexp -- key is escaped and one of our constants
  const match = raw.match(new RegExp(`"${escapeRegExp(key)}"\\s*(?::|=>)\\s*(?:"([^"]*)"|([\\d.]+))`));
  if (!match) return null;
  return match[1] ?? match[2] ?? null;
};

/**
 * A line built by the personalisation journey (see personalisation/src/gateway/createPayload.ts). Its
 * `_personalisation_choices` can be empty ("[]") when only the variant was picked, so the property being there,
 * or the base attributes, is what counts. Add-on lines expanded by the cart transform are skipped.
 */
export const isPersonalisedLine = (line: CartLine): boolean => {
  const properties = line.properties;
  if (!properties || properties._add_on_product === 'true') return false;
  return (
    (properties._personalisation_choices !== undefined && properties._personalisation_choices !== null) ||
    isPresent(properties._personalisation_base_attributes)
  );
};

/** The version a line was added under. Unstamped lines count as version 1. */
export const getLineVersion = (line: CartLine): number => toVersion(readBaseAttribute(line, CART_VERSION_KEY));

/**
 * A personalised line stamped below its product's current version.
 * @param currentVersion - The product's current cart version. Missing = 1, so unstamped lines stay.
 */
export const isStaleLine = (line: CartLine, currentVersion: number | null | undefined): boolean =>
  isPersonalisedLine(line) && getLineVersion(line) < toVersion(currentVersion);

/**
 * Finds personalised lines stamped below their product's current version.
 * @param versionsByProductId - Current cart version per product ID (from fetchCartVersionData()).
 */
export const findStaleLines = (lines: CartLine[], versionsByProductId: Record<string, number>): CartLine[] =>
  lines.filter((line) => isStaleLine(line, versionsByProductId[String(line.product_id ?? '')]));

/** Normalises cart version data rendered by snippets/cart-version-data.liquid. */
export const parseCartVersionData = (data: Partial<CartVersionData> | null | undefined): CartVersionData => ({
  enabled: data?.enabled === true,
  versions: Object.fromEntries(
    Object.entries(data?.versions ?? {}).map(([productId, version]) => [productId, toVersion(version)])
  )
});

/** Whether the switch is on and any product in the cart is past version 1 (otherwise nothing can be stale). */
export const hasVersionsToEnforce = ({ enabled, versions }: CartVersionData): boolean =>
  enabled && Object.values(versions).some((version) => version > DEFAULT_CART_VERSION);

/**
 * Fetches cart version data for the current cart (sections/cart-version-data.liquid), e.g. on the
 * recover page after the cart has been rebuilt.
 */
export const fetchCartVersionData = async (): Promise<CartVersionData> => {
  const response = await fetch('/?sections=cart-version-data');
  if (!response.ok) throw new Error(`Failed to fetch cart version data: ${response.status}`);
  const sections = await response.json();
  const html = sections?.['cart-version-data'];
  if (typeof html !== 'string') throw new Error('Cart version data section not rendered');

  const script = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-version-data]');
  return parseCartVersionData(JSON.parse(script?.textContent || '{}'));
};
